import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { restaurantAPI, cityAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import useLocation from '../../hooks/useLocation';
import { validatePAN, validateGST, validateFSSAI, validateIFSC } from '../../utils/documentValidation';
import TimeInput12h from '../../components/TimeInput12h';

const CUISINE_TYPES = [
  'Indian', 'Chinese', 'Italian', 'Mexican', 'Thai', 'Japanese',
  'American', 'Continental', 'Fast Food', 'Desserts', 'Bakery',
  'South Indian', 'North Indian', 'Bengali', 'Punjabi', 'Mughlai'
];

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function RestaurantOnboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getAddressFromCurrentLocation, loading: locationLoading } = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [restaurantExists, setRestaurantExists] = useState(false);
  const [availableCities, setAvailableCities] = useState([]);

  // Step 1: Basic Restaurant Information
  const [basicInfo, setBasicInfo] = useState({
    name: '',
    description: '',
    phone: '',
    email: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    lat: '',
    lng: ''
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState('');

  // Step 2: Operational Details
  const [menuImages, setMenuImages] = useState([]);
  const [menuImagePreviews, setMenuImagePreviews] = useState([]);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [openingHours, setOpeningHours] = useState({
    monday: { open: '09:00', close: '22:00', isOff: false },
    tuesday: { open: '09:00', close: '22:00', isOff: false },
    wednesday: { open: '09:00', close: '22:00', isOff: false },
    thursday: { open: '09:00', close: '22:00', isOff: false },
    friday: { open: '09:00', close: '22:00', isOff: false },
    saturday: { open: '09:00', close: '22:00', isOff: false },
    sunday: { open: '09:00', close: '22:00', isOff: false }
  });
  const [deliveryTimings, setDeliveryTimings] = useState({
    preparationTime: 30,
    deliveryRadius: 5,
    estimatedDeliveryTime: 45
  });
  const [deviceType, setDeviceType] = useState('web_dashboard');

  // Step 3: Documents
  const [documents, setDocuments] = useState({
    pan: { number: '', holderName: '', file: null },
    gst: { number: '', businessName: '', file: null },
    fssai: { number: '', licenseName: '', address: '', expiryDate: '', file: null },
    bankAccount: { accountNumber: '', accountHolderName: '', ifscCode: '', bankName: '', branchName: '' }
  });
  const [gstExempt, setGstExempt] = useState(false);
  const [gstDeclaration, setGstDeclaration] = useState({ ownerName: '', businessName: '', businessAddress: '' });

  // Step 4: Contract
  const [contractAccepted, setContractAccepted] = useState(false);
  const [contractSubmitted, setContractSubmitted] = useState(false);
  const [contractUrl, setContractUrl] = useState('');

  // Check for pending registration data from static registration page
  useEffect(() => {
    try {
      const pendingData = sessionStorage.getItem('pendingRestaurantRegistration');
      if (pendingData) {
        const { restaurantName, ownerName, email, phone } = JSON.parse(pendingData);
        // Pre-fill form with data from static registration page
        setBasicInfo(prev => ({
          ...prev,
          name: restaurantName || prev.name,
          email: email || prev.email,
          phone: phone || prev.phone
        }));
        // Clear the sessionStorage after using the data
        sessionStorage.removeItem('pendingRestaurantRegistration');
      }
    } catch (err) {
      console.log('No pending registration data found');
    }
  }, []);

  useEffect(() => {
    fetchRestaurantData();
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      const response = await cityAPI.getActiveCities();
      setAvailableCities(response.data.data.cities);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  const fetchRestaurantData = async () => {
    try {
      const response = await restaurantAPI.getMyRestaurant();
      const restaurant = response.data.data.restaurant;

      if (restaurant) {
        // Check if restaurant is approved - redirect to plan selection
        if (restaurant.isApproved || restaurant.approvalStatus === 'approved') {
          // Check if plan already selected
          const planStatus = restaurant.subscriptionPlan?.type;
          if (planStatus && planStatus !== 'none') {
            // Plan already selected, go to dashboard
            navigate('/restaurant/dashboard');
          } else {
            // Plan not selected, go to plan selection
            navigate('/restaurant/plan-selection');
          }
          return;
        }

        // Restaurant exists - pre-fill data and set flag
        setRestaurantExists(true);
        setCurrentStep(restaurant.onboardingStatus?.step || 2);

        // Pre-fill basic info
        if (restaurant.name) setBasicInfo({
          name: restaurant.name,
          description: restaurant.description || '',
          phone: restaurant.phone || '',
          email: restaurant.email || '',
          ownerName: restaurant.ownerName || '',
          ownerEmail: restaurant.ownerEmail || '',
          ownerPhone: restaurant.ownerPhone || '',
          address: restaurant.address || basicInfo.address,
          lat: restaurant.location?.lat || '',
          lng: restaurant.location?.lng || ''
        });

        // Pre-fill operational details
        if (restaurant.cuisineType) setSelectedCuisines(restaurant.cuisineType);
        if (restaurant.openingHours) setOpeningHours(restaurant.openingHours);
        if (restaurant.deliveryTimings) setDeliveryTimings(restaurant.deliveryTimings);
        if (restaurant.deviceType) setDeviceType(restaurant.deviceType);

        // Pre-fill documents including bank account
        if (restaurant.documents) {
          const docs = restaurant.documents;
          setDocuments(prev => ({
            pan: {
              number: docs.pan?.number || '',
              holderName: docs.pan?.holderName || '',
              file: null
            },
            gst: {
              number: docs.gst?.number || '',
              businessName: docs.gst?.businessName || '',
              file: null
            },
            fssai: {
              number: docs.fssai?.number || '',
              licenseName: docs.fssai?.licenseName || '',
              address: docs.fssai?.address || '',
              expiryDate: docs.fssai?.expiryDate || '',
              file: null
            },
            bankAccount: {
              accountNumber: docs.bankAccount?.accountNumber || '',
              accountHolderName: docs.bankAccount?.accountHolderName || '',
              ifscCode: docs.bankAccount?.ifscCode || '',
              bankName: docs.bankAccount?.bankName || '',
              branchName: docs.bankAccount?.branchName || ''
            }
          }));

        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // Expected for new restaurant owners - no restaurant record yet
        console.log('No restaurant found - starting fresh onboarding at Step 1');
        setRestaurantExists(false);
        setCurrentStep(1);
      } else if (error.response?.status === 403) {
        // Authorization error - user may not have restaurant role
        console.error('Authorization error - user may not have restaurant role:', error);
        setError('You do not have permission to access the restaurant portal. Please login with a restaurant account or contact support.');
      } else if (error.response?.status === 401) {
        // Authentication error - token expired or invalid
        console.error('Authentication error - token may be expired:', error);
        setError('Your session has expired. Please login again.');
        // Clear token and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      } else {
        // Other error - show to user
        console.error('Error fetching restaurant data:', error);
        setError('Failed to load restaurant data. Please refresh the page or try logging in again.');
      }
    }
  };

  const handleMenuImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setMenuImages([...menuImages, ...files]);

    // Create previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setMenuImagePreviews([...menuImagePreviews, ...newPreviews]);
  };

  const removeMenuImage = (index) => {
    setMenuImages(menuImages.filter((_, i) => i !== index));
    setMenuImagePreviews(menuImagePreviews.filter((_, i) => i !== index));
  };

  const toggleCuisine = (cuisine) => {
    if (selectedCuisines.includes(cuisine)) {
      setSelectedCuisines(selectedCuisines.filter(c => c !== cuisine));
    } else {
      setSelectedCuisines([...selectedCuisines, cuisine]);
    }
  };

  const updateOpeningHours = (day, field, value) => {
    setOpeningHours({
      ...openingHours,
      [day]: { ...openingHours[day], [field]: value }
    });
  };

  const handleDocumentFileUpload = (docType, file) => {
    setDocuments({
      ...documents,
      [docType]: { ...documents[docType], file }
    });
  };

  const validateStep = async () => {
    if (currentStep === 1) {
      if (!basicInfo.name.trim()) {
        setError('Please enter restaurant name');
        return false;
      }
      if (!basicInfo.description.trim()) {
        setError('Please enter restaurant description');
        return false;
      }
      if (!basicInfo.email.trim()) {
        setError('Please enter restaurant email');
        return false;
      }
      if (!basicInfo.ownerName?.trim()) {
        setError('Please enter owner full name');
        return false;
      }
      if (!basicInfo.ownerEmail?.trim()) {
        setError('Please enter owner email');
        return false;
      }
      if (!basicInfo.phone.trim()) {
        setError('Please enter phone number');
        return false;
      }
      if (!basicInfo.address.street.trim() || !basicInfo.address.city.trim() ||
          !basicInfo.address.state.trim() || !basicInfo.address.zipCode.trim()) {
        setError('Please complete all address fields');
        return false;
      }

      // Validate GPS coordinates
      if (!basicInfo.lat || !basicInfo.lng) {
        setError('Please provide GPS coordinates. Use "Use Current Location" button or enter manually.');
        return false;
      }

      // Validate that coordinates are within the selected city
      try {
        setLoading(true);
        const response = await cityAPI.validateCoordinates({
          lat: basicInfo.lat,
          lng: basicInfo.lng,
          cityName: basicInfo.address.city,
          stateName: basicInfo.address.state
        });

        if (!response.data.success) {
          setError(response.data.message || 'Invalid coordinates for the selected city');
          setLoading(false);
          return false;
        }
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to validate location. Please ensure your coordinates match the selected city.');
        setLoading(false);
        return false;
      }
    }

    if (currentStep === 2) {
      if (selectedCuisines.length === 0) {
        setError('Please select at least one cuisine type');
        return false;
      }
      if (menuImages.length === 0) {
        setError('Please upload at least one menu image');
        return false;
      }
    }

    if (currentStep === 3) {
      // Validate PAN
      if (!documents.pan.number || !documents.pan.holderName || !documents.pan.file) {
        setError('Please complete PAN details');
        return false;
      }

      // Validate PAN format
      const panValidation = validatePAN(documents.pan.number);
      if (!panValidation.valid) {
        setError(panValidation.message);
        return false;
      }

      // Validate GST (skip if exempt)
      if (!gstExempt) {
        if (!documents.gst.number || !documents.gst.businessName || !documents.gst.file) {
          setError('Please complete GST details');
          return false;
        }
        const gstValidation = validateGST(documents.gst.number, documents.pan.number);
        if (!gstValidation.valid) {
          setError(gstValidation.message);
          return false;
        }
      } else {
        // Validate declaration fields
        if (!gstDeclaration.ownerName || !gstDeclaration.businessName || !gstDeclaration.businessAddress) {
          setError('Please complete the GST exemption declaration');
          return false;
        }
      }

      // Validate FSSAI
      if (!documents.fssai.number || !documents.fssai.licenseName || !documents.fssai.address || !documents.fssai.file) {
        setError('Please complete FSSAI details');
        return false;
      }

      // Validate FSSAI format
      const fssaiValidation = validateFSSAI(documents.fssai.number);
      if (!fssaiValidation.valid) {
        setError(fssaiValidation.message);
        return false;
      }

      // Validate Bank Account
      if (!documents.bankAccount.accountNumber || !documents.bankAccount.accountHolderName ||
          !documents.bankAccount.ifscCode || !documents.bankAccount.bankName) {
        setError('Please complete bank account details');
        return false;
      }

      // Validate IFSC code format
      const ifscValidation = validateIFSC(documents.bankAccount.ifscCode);
      if (!ifscValidation.valid) {
        setError(ifscValidation.message);
        return false;
      }
    }

    if (currentStep === 4) {
      if (!contractAccepted) {
        setError('Please accept the contract to proceed');
        return false;
      }
    }

    return true;
  };

  const handleNext = async () => {
    setError('');

    if (!(await validateStep())) {
      return;
    }

    if (currentStep < 4) {
      // Save current step data before moving forward
      await saveStepData();
      setCurrentStep(currentStep + 1);
    } else {
      // Final submission
      await handleFinalSubmit();
    }
  };

  const saveStepData = async () => {
    setLoading(true);
    try {
      if (currentStep === 1) {
        const formData = new FormData();

        formData.append('name', basicInfo.name);
        formData.append('description', basicInfo.description);
        formData.append('email', basicInfo.email);
        formData.append('phone', basicInfo.phone);
        if (basicInfo.ownerName) formData.append('ownerName', basicInfo.ownerName);
        if (basicInfo.ownerEmail) formData.append('ownerEmail', basicInfo.ownerEmail);
        if (basicInfo.ownerPhone) formData.append('ownerPhone', basicInfo.ownerPhone || basicInfo.phone);
        formData.append('address', JSON.stringify(basicInfo.address));
        formData.append('location', JSON.stringify({
          lat: parseFloat(basicInfo.lat) || 0,
          lng: parseFloat(basicInfo.lng) || 0
        }));

        if (profilePhoto) {
          formData.append('image', profilePhoto);
        }

        if (!restaurantExists) {
          await restaurantAPI.createWithFormData(formData);
          setRestaurantExists(true);
        }
        // Owner fields are saved at Step 4 (contract acceptance) to
        // handle both new and existing restaurants consistently.
        return;
      }

      const formData = new FormData();

      if (currentStep === 2) {
        // Upload menu images
        menuImages.forEach((file) => {
          formData.append('menuImages', file);
        });

        formData.append('cuisineType', JSON.stringify(selectedCuisines));
        formData.append('openingHours', JSON.stringify(openingHours));
        formData.append('deliveryTimings', JSON.stringify(deliveryTimings));
        formData.append('deviceType', deviceType);

        await restaurantAPI.updateOperationalDetails(formData);
      }

      if (currentStep === 3) {
        // Upload documents
        if (documents.pan.file) formData.append('panDocument', documents.pan.file);
        if (documents.gst.file) formData.append('gstDocument', documents.gst.file);
        if (documents.fssai.file) formData.append('fssaiDocument', documents.fssai.file);

        formData.append('pan', JSON.stringify({ number: documents.pan.number, holderName: documents.pan.holderName }));
        if (gstExempt) {
          formData.append('gst', JSON.stringify({ exempt: true, exemptDeclaration: gstDeclaration }));
        } else {
          formData.append('gst', JSON.stringify({ number: documents.gst.number, businessName: documents.gst.businessName }));
        }
        formData.append('fssai', JSON.stringify({
          number: documents.fssai.number,
          licenseName: documents.fssai.licenseName,
          address: documents.fssai.address,
          expiryDate: documents.fssai.expiryDate
        }));
        formData.append('bankAccount', JSON.stringify(documents.bankAccount));

        await restaurantAPI.submitDocuments(formData);
      }

    } catch (error) {
      console.error('Error saving step data:', error);
      setError(error.response?.data?.message || 'Failed to save data');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    try {
      const response = await restaurantAPI.submitContract({
        contractAccepted,
        // Send owner identity with contract acceptance — ensures the
        // contract PDF uses the correct owner name even if Step 1 was
        // skipped (resuming onboarding for existing restaurants).
        ownerName: basicInfo.ownerName,
        ownerEmail: basicInfo.ownerEmail,
        ownerPhone: basicInfo.ownerPhone || basicInfo.phone,
      });
      const { contractUrl: generatedContractUrl } = response.data.data;

      setContractSubmitted(true);
      setContractUrl(generatedContractUrl);
    } catch (error) {
      console.error('Error submitting onboarding:', error);
      setError(error.response?.data?.message || 'Failed to submit onboarding');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadContract = async () => {
    try {
      const response = await restaurantAPI.downloadContract();

      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'partner-agreement.pdf';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading contract:', error);
      setError('Failed to download contract. Please try again.');
    }
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDetectLocation = async () => {
    try {
      // First, get GPS coordinates (this always works if permission granted)
      const position = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by your browser'));
          return;
        }

        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;

      // Try to get address details (but don't fail if this doesn't work)
      let addressDetails = null;
      try {
        addressDetails = await getAddressFromCurrentLocation();
      } catch (addressError) {
        console.warn('Could not get address details, but GPS coordinates were obtained:', addressError);
      }

      // Update form with GPS coordinates and address if available
      setBasicInfo(prev => ({
        ...prev,
        ...(addressDetails && {
          address: {
            ...prev.address,
            street: addressDetails.street || prev.address.street,
            city: addressDetails.city || prev.address.city,
            state: addressDetails.state || prev.address.state,
            zipCode: addressDetails.zipCode || prev.address.zipCode,
          }
        }),
        lat: lat,
        lng: lng
      }));

      if (addressDetails) {
        alert('Location and GPS coordinates detected successfully!');
      } else {
        alert('GPS coordinates detected successfully!\n\nNote: Could not auto-detect address details. Please enter your street address, city, state, and ZIP code manually.');
      }
    } catch (error) {
      let errorMessage = 'Failed to detect location. ';
      if (error.code === 1) {
        errorMessage += 'Please enable location permissions in your browser settings.';
      } else if (error.code === 2) {
        errorMessage += 'Location information is unavailable.';
      } else if (error.code === 3) {
        errorMessage += 'Location request timed out.';
      } else {
        errorMessage += error.message || 'Please ensure location permissions are enabled.';
      }
      alert(errorMessage);
    }
  };

  const renderProgressBar = () => {
    const steps = [
      { number: 1, title: 'Basic Info' },
      { number: 2, title: 'Operational Details' },
      { number: 3, title: 'Documents' },
      { number: 4, title: 'Review & Contract' }
    ];

    return (
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', position: 'relative' }}>
          {/* Connecting Line */}
          <div style={{
            position: 'absolute',
            top: '24px',
            left: '10%',
            right: '10%',
            height: '2px',
            background: '#E2E8F0',
            zIndex: 0
          }}>
            <div style={{
              height: '100%',
              width: `${((currentStep - 1) / 3) * 100}%`,
              background: '#FF6961',
              transition: 'width 0.3s ease'
            }} />
          </div>

          {steps.map((step) => (
            <div key={step.number} style={{ flex: 1, textAlign: 'center', position: 'relative', zIndex: 1 }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: currentStep >= step.number ? '#FF6961' : '#F8FAFC',
                border: currentStep >= step.number ? 'none' : '2px solid #E2E8F0',
                color: currentStep >= step.number ? '#FFFFFF' : '#94A3B8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '18px',
                margin: '0 auto 8px',
                transition: 'all 0.3s ease',
                boxShadow: currentStep >= step.number ? '0 2px 8px rgba(255, 105, 97, 0.3)' : 'none'
              }}>
                {currentStep > step.number ? '✓' : step.number}
              </div>
              <p style={{
                fontSize: '12px',
                color: currentStep >= step.number ? '#0F172A' : '#64748B',
                fontWeight: currentStep >= step.number ? '600' : '500',
                margin: 0,
                lineHeight: '16px'
              }}>
                {step.title}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStep1 = () => (
    <div>
      <h2 style={{
        fontSize: '24px',
        fontWeight: '700',
        color: '#0F172A',
        marginBottom: '24px',
        letterSpacing: '-0.5px'
      }}>
        🏪 Basic Restaurant Information
      </h2>

      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '600',
          color: '#0F172A',
          marginBottom: '8px'
        }}>Restaurant Name *</label>
        <input
          type="text"
          placeholder="Enter your restaurant name"
          value={basicInfo.name}
          onChange={(e) => setBasicInfo({ ...basicInfo, name: e.target.value })}
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: '15px',
            color: '#0F172A',
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            outline: 'none',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => e.target.style.borderColor = '#FF6961'}
          onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '600',
          color: '#0F172A',
          marginBottom: '8px'
        }}>Restaurant Description *</label>
        <textarea
          rows="4"
          placeholder="Describe your restaurant, cuisine specialties, and what makes you unique"
          value={basicInfo.description}
          onChange={(e) => setBasicInfo({ ...basicInfo, description: e.target.value })}
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: '15px',
            color: '#0F172A',
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            outline: 'none',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
          onFocus={(e) => e.target.style.borderColor = '#FF6961'}
          onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '600',
          color: '#0F172A',
          marginBottom: '8px'
        }}>Restaurant Email *</label>
        <input
          type="email"
          placeholder="restaurant@example.com"
          value={basicInfo.email}
          onChange={(e) => setBasicInfo({ ...basicInfo, email: e.target.value })}
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: '15px',
            color: '#0F172A',
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            outline: 'none',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => e.target.style.borderColor = '#FF6961'}
          onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '600',
          color: '#0F172A',
          marginBottom: '8px'
        }}>Phone Number *</label>
        <input
          type="tel"
          placeholder="+91 1234567890"
          value={basicInfo.phone}
          onChange={(e) => setBasicInfo({ ...basicInfo, phone: e.target.value })}
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: '15px',
            color: '#0F172A',
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            outline: 'none',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => e.target.style.borderColor = '#FF6961'}
          onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
        />
      </div>

      {/* Owner Details Section */}
      <div style={{
        padding: '16px',
        background: '#F0FDF4',
        borderRadius: '10px',
        marginBottom: '20px',
        border: '1px solid #BBF7D0'
      }}>
        <h4 style={{ margin: '0 0 12px', color: '#166534', fontSize: '15px' }}>Owner / Partner Details</h4>
        <p style={{ fontSize: '12px', color: '#4ADE80', marginTop: '-8px', marginBottom: '14px' }}>
          These details will appear on your partner agreement and invoices
        </p>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0F172A', marginBottom: '6px' }}>Owner Full Name *</label>
          <input
            type="text"
            placeholder="Full legal name of the restaurant owner"
            value={basicInfo.ownerName}
            onChange={(e) => setBasicInfo({ ...basicInfo, ownerName: e.target.value })}
            style={{ width: '100%', padding: '10px 14px', fontSize: '14px', border: '1px solid #E2E8F0', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }}
            onFocus={(e) => e.target.style.borderColor = '#FF6961'}
            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
          />
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0F172A', marginBottom: '6px' }}>Owner Email *</label>
          <input
            type="email"
            placeholder="owner@example.com"
            value={basicInfo.ownerEmail}
            onChange={(e) => setBasicInfo({ ...basicInfo, ownerEmail: e.target.value })}
            style={{ width: '100%', padding: '10px 14px', fontSize: '14px', border: '1px solid #E2E8F0', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }}
            onFocus={(e) => e.target.style.borderColor = '#FF6961'}
            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0F172A', marginBottom: '6px' }}>Owner Phone</label>
          <input
            type="tel"
            placeholder="Same as restaurant phone if not different"
            value={basicInfo.ownerPhone}
            onChange={(e) => setBasicInfo({ ...basicInfo, ownerPhone: e.target.value })}
            style={{ width: '100%', padding: '10px 14px', fontSize: '14px', border: '1px solid #E2E8F0', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }}
            onFocus={(e) => e.target.style.borderColor = '#FF6961'}
            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
          />
        </div>
      </div>

      {/* Profile Photo Upload */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '600',
          color: '#0F172A',
          marginBottom: '8px'
        }}>📸 Restaurant Profile Photo</label>
        <p style={{
          fontSize: '13px',
          color: '#64748B',
          marginBottom: '12px',
          marginTop: '4px'
        }}>
          Upload a photo of your restaurant (optional but recommended)
        </p>
        <input
          type="file"
          accept="image/*"
          onChange={handleProfilePhotoChange}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '14px',
            color: '#0F172A',
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            marginBottom: '16px'
          }}
        />
        {profilePhotoPreview && (
          <div style={{ position: 'relative', width: 'min(200px, 100%)', marginTop: '12px' }}>
            <img
              src={profilePhotoPreview}
              alt="Profile Preview"
              style={{
                width: 'min(200px, 100%)',
                height: 'min(200px, 50vw)',
                objectFit: 'cover',
                borderRadius: '12px',
                border: '2px solid #E2E8F0'
              }}
            />
            <button
              type="button"
              onClick={() => {
                setProfilePhoto(null);
                setProfilePhotoPreview('');
              }}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'rgba(255, 105, 97, 0.95)',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>

      <h3 style={{
        fontSize: '20px',
        fontWeight: '700',
        color: '#0F172A',
        marginTop: '32px',
        marginBottom: '20px',
        letterSpacing: '-0.3px'
      }}>
        📍 Restaurant Address
      </h3>

      {/* Location Detection Button */}
      <button
        type="button"
        onClick={handleDetectLocation}
        disabled={locationLoading}
        style={{
          width: '100%',
          padding: '14px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          background: locationLoading ? '#94A3B8' : '#FF6961',
          color: '#FFFFFF',
          border: 'none',
          borderRadius: '8px',
          fontSize: '15px',
          fontWeight: '600',
          cursor: locationLoading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: locationLoading ? 'none' : '0 2px 8px rgba(255, 105, 97, 0.25)'
        }}
        onMouseEnter={(e) => !locationLoading && (e.target.style.background = '#FF5449')}
        onMouseLeave={(e) => !locationLoading && (e.target.style.background = '#FF6961')}
      >
        <span>{locationLoading ? '🔄' : '📍'}</span>
        <span>{locationLoading ? 'Detecting Location...' : 'Use Current Location'}</span>
      </button>

      <div className="form-group">
        <label>Street Address *</label>
        <input
          type="text"
          placeholder="Building name, street, area"
          value={basicInfo.address.street}
          onChange={(e) => setBasicInfo({
            ...basicInfo,
            address: { ...basicInfo.address, street: e.target.value }
          })}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--space-md)' }}>
        <div className="form-group">
          <label>City *</label>
          <select
            value={basicInfo.address.city}
            onChange={(e) => {
              const selectedCity = availableCities.find(city => city.name === e.target.value);
              setBasicInfo({
                ...basicInfo,
                address: {
                  ...basicInfo.address,
                  city: e.target.value,
                  state: selectedCity ? selectedCity.state : basicInfo.address.state
                }
              });
            }}
            required
          >
            <option value="">Select City</option>
            {availableCities.map((city, index) => (
              <option key={index} value={city.name}>
                {city.name}, {city.state}
              </option>
            ))}
          </select>
          {availableCities.length === 0 && (
            <p style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
              marginTop: 'var(--space-xs)'
            }}>
              No cities available. Please contact admin.
            </p>
          )}
        </div>

        <div className="form-group">
          <label>State *</label>
          <input
            type="text"
            placeholder="State (auto-filled)"
            value={basicInfo.address.state}
            readOnly
            style={{
              backgroundColor: 'var(--color-background-secondary)',
              cursor: 'not-allowed'
            }}
          />
        </div>

        <div className="form-group">
          <label>ZIP Code *</label>
          <input
            type="text"
            placeholder="PIN Code"
            value={basicInfo.address.zipCode}
            onChange={(e) => setBasicInfo({
              ...basicInfo,
              address: { ...basicInfo.address, zipCode: e.target.value }
            })}
            maxLength="6"
          />
        </div>
      </div>

      {/* GPS Coordinates */}
      <div style={{
        marginTop: 'var(--space-lg)',
        paddingTop: 'var(--space-lg)',
        borderTop: '2px solid var(--gray-200)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)',
          marginBottom: 'var(--space-md)'
        }}>
          <span style={{ fontSize: 'var(--font-size-lg)' }}>🌍</span>
          <span style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: '600',
            color: 'var(--gray-700)'
          }}>
            GPS Coordinates (for delivery navigation)
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
          <div className="form-group">
            <label>Latitude</label>
            <input
              type="number"
              step="any"
              value={basicInfo.lat}
              onChange={(e) => setBasicInfo({ ...basicInfo, lat: e.target.value })}
              onWheel={(e) => e.target.blur()}
              placeholder="27.1751"
            />
          </div>

          <div className="form-group">
            <label>Longitude</label>
            <input
              type="number"
              step="any"
              value={basicInfo.lng}
              onChange={(e) => setBasicInfo({ ...basicInfo, lng: e.target.value })}
              onWheel={(e) => e.target.blur()}
              placeholder="78.0421"
            />
          </div>
        </div>
        <p style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--gray-600)',
          marginTop: 'var(--space-xs)',
          fontStyle: 'italic'
        }}>
          💡 Tip: Click "Use Current Location" button above to auto-detect GPS coordinates
        </p>
      </div>

      <div style={{
        background: 'rgba(79, 70, 229, 0.05)',
        border: '2px solid var(--primary)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-lg)',
        marginTop: 'var(--space-2xl)'
      }}>
        <h4 style={{ marginTop: 0, color: 'var(--primary)' }}>📝 Important</h4>
        <p style={{ marginBottom: 0, color: 'var(--gray-700)' }}>
          Make sure your restaurant address matches exactly with your FSSAI license address.
          This information will be verified during the approval process.
        </p>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h2 style={{ marginBottom: 'var(--space-xl)', color: 'var(--gray-900)' }}>
        📋 Operational Details
      </h2>

      {/* Menu Images Upload */}
      <div className="form-group">
        <label>🍽️ Delivery Menu Images *</label>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginTop: '-var(--space-sm)' }}>
          These will be shown in your in-app menu for online ordering
        </p>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleMenuImageUpload}
          style={{ marginBottom: 'var(--space-md)' }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 'var(--space-md)' }}>
          {menuImagePreviews.map((preview, index) => (
            <div key={index} style={{ position: 'relative' }}>
              <img
                src={preview}
                alt={`Menu ${index + 1}`}
                style={{
                  width: '100%',
                  height: '150px',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-md)',
                  border: '2px solid var(--gray-200)'
                }}
              />
              <button
                type="button"
                onClick={() => removeMenuImage(index)}
                style={{
                  position: 'absolute',
                  top: 'var(--space-xs)',
                  right: 'var(--space-xs)',
                  background: 'var(--danger)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-lg)'
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Cuisine Type */}
      <div className="form-group">
        <label>🍴 Choose Your Cuisine Type *</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
          {CUISINE_TYPES.map((cuisine) => (
            <div
              key={cuisine}
              onClick={() => toggleCuisine(cuisine)}
              style={{
                padding: 'var(--space-sm) var(--space-lg)',
                borderRadius: 'var(--radius-full)',
                border: `2px solid ${selectedCuisines.includes(cuisine) ? 'var(--primary)' : 'var(--gray-300)'}`,
                background: selectedCuisines.includes(cuisine) ? 'rgba(79, 70, 229, 0.1)' : 'white',
                color: selectedCuisines.includes(cuisine) ? 'var(--primary)' : 'var(--gray-700)',
                cursor: 'pointer',
                fontWeight: selectedCuisines.includes(cuisine) ? '700' : '500',
                fontSize: 'var(--font-size-sm)',
                transition: 'all var(--transition-base)'
              }}
            >
              {cuisine}
            </div>
          ))}
        </div>
      </div>

      {/* Operational Hours */}
      <div className="form-group">
        <label>🕐 Set Up Your Operational Hours *</label>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginTop: '-var(--space-sm)', marginBottom: 'var(--space-md)' }}>
          Uncheck boxes for off days
        </p>
        {DAYS_OF_WEEK.map((day) => (
          <div key={day} style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(80px, 100px) 1fr minmax(100px, 150px) minmax(100px, 150px) minmax(60px, 80px)',
            gap: 'var(--space-sm)',
            alignItems: 'center',
            marginBottom: 'var(--space-md)',
            padding: 'var(--space-md)',
            background: openingHours[day].isOff ? 'var(--gray-100)' : 'white',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--gray-200)'
          }}
          className="opening-hours-row">
            <div style={{ fontWeight: '600', textTransform: 'capitalize', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>{day}</div>
            <div />
            <TimeInput12h
              value={openingHours[day].open}
              onChange={(val) => updateOpeningHours(day, 'open', val)}
            />
            <TimeInput12h
              value={openingHours[day].close}
              onChange={(val) => updateOpeningHours(day, 'close', val)}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={openingHours[day].isOff}
                onChange={(e) => updateOpeningHours(day, 'isOff', e.target.checked)}
              />
              <span style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>Off</span>
            </label>
          </div>
        ))}
      </div>

      {/* Delivery Timings */}
      <div className="form-group">
        <label>⏱️ Add Delivery Timings Clearly *</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--space-md)' }}>
          <div>
            <label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)' }}>Preparation Time (minutes)</label>
            <input
              type="number"
              value={deliveryTimings.preparationTime}
              onChange={(e) => setDeliveryTimings({ ...deliveryTimings, preparationTime: parseInt(e.target.value) })}
              onWheel={(e) => e.target.blur()}
              min="10"
              max="120"
            />
          </div>
          <div>
            <label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)' }}>Delivery Radius (km)</label>
            <input
              type="number"
              value={deliveryTimings.deliveryRadius}
              onChange={(e) => setDeliveryTimings({ ...deliveryTimings, deliveryRadius: parseInt(e.target.value) })}
              onWheel={(e) => e.target.blur()}
              min="1"
              max="20"
            />
          </div>
          <div>
            <label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)' }}>Estimated Delivery Time (minutes)</label>
            <input
              type="number"
              value={deliveryTimings.estimatedDeliveryTime}
              onChange={(e) => setDeliveryTimings({ ...deliveryTimings, estimatedDeliveryTime: parseInt(e.target.value) })}
              onWheel={(e) => e.target.blur()}
              min="20"
              max="180"
            />
          </div>
        </div>
      </div>

      {/* Device Type */}
      <div className="form-group">
        <label>📱 Select the Device Type You Will Use for Order Acceptance *</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-md)' }}>
          <label style={{
            padding: 'var(--space-lg)',
            border: `3px solid ${deviceType === 'mobile_app' ? 'var(--primary)' : 'var(--gray-200)'}`,
            borderRadius: 'var(--radius-lg)',
            cursor: 'pointer',
            background: deviceType === 'mobile_app' ? 'rgba(79, 70, 229, 0.05)' : 'white',
            transition: 'all var(--transition-base)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)'
          }}>
            <input
              type="radio"
              name="deviceType"
              value="mobile_app"
              checked={deviceType === 'mobile_app'}
              onChange={(e) => setDeviceType(e.target.value)}
            />
            <div>
              <div style={{ fontWeight: '700', fontSize: 'var(--font-size-lg)', marginBottom: 'var(--space-xs)' }}>
                📱 Foodwinc Partner Mobile App
              </div>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', margin: 0 }}>
                Manage orders on the go
              </p>
            </div>
          </label>

          <label style={{
            padding: 'var(--space-lg)',
            border: `3px solid ${deviceType === 'web_dashboard' ? 'var(--primary)' : 'var(--gray-200)'}`,
            borderRadius: 'var(--radius-lg)',
            cursor: 'pointer',
            background: deviceType === 'web_dashboard' ? 'rgba(79, 70, 229, 0.05)' : 'white',
            transition: 'all var(--transition-base)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)'
          }}>
            <input
              type="radio"
              name="deviceType"
              value="web_dashboard"
              checked={deviceType === 'web_dashboard'}
              onChange={(e) => setDeviceType(e.target.value)}
            />
            <div>
              <div style={{ fontWeight: '700', fontSize: 'var(--font-size-lg)', marginBottom: 'var(--space-xs)' }}>
                💻 Foodwinc Desktop Web Dashboard
              </div>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', margin: 0 }}>
                Full-featured desktop experience
              </p>
            </div>
          </label>

          <label style={{
            padding: 'var(--space-lg)',
            border: `3px solid ${deviceType === 'pos_system' ? 'var(--primary)' : 'var(--gray-200)'}`,
            borderRadius: 'var(--radius-lg)',
            cursor: 'pointer',
            background: deviceType === 'pos_system' ? 'rgba(79, 70, 229, 0.05)' : 'white',
            transition: 'all var(--transition-base)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)'
          }}>
            <input
              type="radio"
              name="deviceType"
              value="pos_system"
              checked={deviceType === 'pos_system'}
              onChange={(e) => setDeviceType(e.target.value)}
            />
            <div>
              <div style={{ fontWeight: '700', fontSize: 'var(--font-size-lg)', marginBottom: 'var(--space-xs)' }}>
                🖥️ Point of Sale System [POS]
              </div>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', margin: 0 }}>
                Integrated with your POS
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h2 style={{ marginBottom: 'var(--space-xl)', color: 'var(--gray-900)' }}>
        📄 Restaurant Documents
      </h2>

      <div style={{
        background: 'rgba(255, 184, 77, 0.1)',
        border: '2px solid var(--warning)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-lg)',
        marginBottom: 'var(--space-2xl)'
      }}>
        <p style={{ margin: 0, color: 'var(--gray-800)', fontWeight: '600' }}>
          ⚠️ Important Notes:
        </p>
        <ul style={{ marginTop: 'var(--space-sm)', marginBottom: 0, color: 'var(--gray-700)' }}>
          <li>FSSAI address must match your restaurant's address</li>
          <li>Name on FSSAI license should match either the name on PAN or restaurant name</li>
        </ul>
      </div>

      {/* PAN Details */}
      <div className="card" style={{ marginBottom: 'var(--space-xl)' }}>
        <h3 style={{ marginBottom: 'var(--space-lg)' }}>🏛️ PAN Card Details</h3>
        <div className="form-group">
          <label>PAN Number *</label>
          <input
            type="text"
            placeholder="ABCDE1234F"
            value={documents.pan.number}
            onChange={(e) => setDocuments({
              ...documents,
              pan: { ...documents.pan, number: e.target.value.toUpperCase() }
            })}
            maxLength="10"
            style={{ textTransform: 'uppercase' }}
          />
        </div>
        <div className="form-group">
          <label>Name on PAN Card *</label>
          <input
            type="text"
            value={documents.pan.holderName}
            onChange={(e) => setDocuments({
              ...documents,
              pan: { ...documents.pan, holderName: e.target.value }
            })}
          />
        </div>
        <div className="form-group">
          <label>Upload PAN Document *</label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => handleDocumentFileUpload('pan', e.target.files[0])}
          />
          {documents.pan.file && (
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--success)', marginTop: 'var(--space-xs)' }}>
              ✓ {documents.pan.file.name}
            </p>
          )}
        </div>
      </div>

      {/* GST Details */}
      <div className="card" style={{ marginBottom: 'var(--space-xl)' }}>
        <h3 style={{ marginBottom: 'var(--space-lg)' }}>📋 GST Details</h3>

        {/* GST Exemption Toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', marginBottom: 'var(--space-lg)',
          background: gstExempt ? '#fef3c7' : '#f0fdf4',
          borderRadius: '10px', border: `1px solid ${gstExempt ? '#f59e0b' : '#86efac'}`
        }}>
          <div>
            <div style={{ fontWeight: '600', fontSize: '14px' }}>
              {gstExempt ? "I don't have a GST number" : 'I have a GST number'}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
              {gstExempt
                ? 'Annual turnover below Rs 20/40 Lakh threshold'
                : 'GST registered business'}
            </div>
          </div>
          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>No GST?</span>
            <input
              type="checkbox"
              checked={gstExempt}
              onChange={(e) => setGstExempt(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
          </label>
        </div>

        {!gstExempt ? (
          <>
            <div className="form-group">
              <label>GST Number *</label>
              <input
                type="text"
                placeholder="22ABCDE1234F1Z5"
                value={documents.gst.number}
                onChange={(e) => setDocuments({
                  ...documents,
                  gst: { ...documents.gst, number: e.target.value.toUpperCase() }
                })}
                maxLength="15"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div className="form-group">
              <label>Business Name (as per GST) *</label>
              <input
                type="text"
                value={documents.gst.businessName}
                onChange={(e) => setDocuments({
                  ...documents,
                  gst: { ...documents.gst, businessName: e.target.value }
                })}
              />
            </div>
            <div className="form-group">
              <label>Upload GST Document *</label>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => handleDocumentFileUpload('gst', e.target.files[0])}
              />
              {documents.gst.file && (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--success)', marginTop: 'var(--space-xs)' }}>
                  ✓ {documents.gst.file.name}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{
              padding: '14px 16px', marginBottom: 'var(--space-lg)',
              background: '#fffbeb', borderRadius: '10px', border: '1px solid #fcd34d'
            }}>
              <p style={{ fontSize: '13px', color: '#92400e', margin: 0, lineHeight: '1.6' }}>
                <strong>GST Exemption Declaration</strong><br/>
                Under Section 9(5) of the CGST Act, FoodWinc will collect and deposit 5% GST to the government on your behalf for all orders placed through the platform. You are declaring that your annual business turnover is below the GST registration threshold (Rs 20 Lakh / Rs 40 Lakh as applicable).
              </p>
            </div>
            <div className="form-group">
              <label>Owner / Proprietor Name *</label>
              <input
                type="text"
                placeholder="Full legal name"
                value={gstDeclaration.ownerName}
                onChange={(e) => setGstDeclaration({ ...gstDeclaration, ownerName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Business / Restaurant Name *</label>
              <input
                type="text"
                placeholder="Restaurant name"
                value={gstDeclaration.businessName}
                onChange={(e) => setGstDeclaration({ ...gstDeclaration, businessName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Business Address *</label>
              <textarea
                placeholder="Complete business address"
                value={gstDeclaration.businessAddress}
                onChange={(e) => setGstDeclaration({ ...gstDeclaration, businessAddress: e.target.value })}
                rows={3}
              />
            </div>
          </>
        )}
      </div>

      {/* FSSAI Details */}
      <div className="card" style={{ marginBottom: 'var(--space-xl)' }}>
        <h3 style={{ marginBottom: 'var(--space-lg)' }}>🍽️ FSSAI License Details</h3>
        <div className="form-group">
          <label>FSSAI License Number *</label>
          <input
            type="text"
            placeholder="12345678901234"
            value={documents.fssai.number}
            onChange={(e) => setDocuments({
              ...documents,
              fssai: { ...documents.fssai, number: e.target.value }
            })}
            maxLength="14"
          />
        </div>
        <div className="form-group">
          <label>Name on FSSAI License *</label>
          <input
            type="text"
            value={documents.fssai.licenseName}
            onChange={(e) => setDocuments({
              ...documents,
              fssai: { ...documents.fssai, licenseName: e.target.value }
            })}
          />
        </div>
        <div className="form-group">
          <label>FSSAI License Address *</label>
          <textarea
            rows="3"
            value={documents.fssai.address}
            onChange={(e) => setDocuments({
              ...documents,
              fssai: { ...documents.fssai, address: e.target.value }
            })}
            placeholder="Must match restaurant address"
          />
        </div>
        <div className="form-group">
          <label>License Expiry Date *</label>
          <input
            type="date"
            value={documents.fssai.expiryDate}
            onChange={(e) => setDocuments({
              ...documents,
              fssai: { ...documents.fssai, expiryDate: e.target.value }
            })}
          />
        </div>
        <div className="form-group">
          <label>Upload FSSAI License *</label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => handleDocumentFileUpload('fssai', e.target.files[0])}
          />
          {documents.fssai.file && (
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--success)', marginTop: 'var(--space-xs)' }}>
              ✓ {documents.fssai.file.name}
            </p>
          )}
        </div>
      </div>

      {/* Bank Account Details */}
      <div className="card" style={{ marginBottom: 'var(--space-xl)' }}>
        <h3 style={{ marginBottom: 'var(--space-lg)' }}>🏦 Bank Account Details</h3>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 'var(--space-md)' }}>
          Bank account will be verified after your restaurant is approved.
        </p>

        <div className="form-group">
          <label>Account Number *</label>
          <input
            type="text"
            value={documents.bankAccount.accountNumber}
            onChange={(e) => setDocuments({
              ...documents,
              bankAccount: { ...documents.bankAccount, accountNumber: e.target.value }
            })}
          />
        </div>
        <div className="form-group">
          <label>Account Holder Name *</label>
          <input
            type="text"
            value={documents.bankAccount.accountHolderName}
            onChange={(e) => setDocuments({
              ...documents,
              bankAccount: { ...documents.bankAccount, accountHolderName: e.target.value }
            })}
          />
        </div>
        <div className="form-group">
          <label>IFSC Code *</label>
          <input
            type="text"
            placeholder="ABCD0123456"
            value={documents.bankAccount.ifscCode}
            onChange={(e) => setDocuments({
              ...documents,
              bankAccount: { ...documents.bankAccount, ifscCode: e.target.value.toUpperCase() }
            })}
            maxLength="11"
            style={{ textTransform: 'uppercase' }}
          />
        </div>
        <div className="form-group">
          <label>Bank Name *</label>
          <input
            type="text"
            value={documents.bankAccount.bankName}
            onChange={(e) => setDocuments({
              ...documents,
              bankAccount: { ...documents.bankAccount, bankName: e.target.value }
            })}
          />
        </div>
        <div className="form-group">
          <label>Branch Name</label>
          <input
            type="text"
            value={documents.bankAccount.branchName}
            onChange={(e) => setDocuments({
              ...documents,
              bankAccount: { ...documents.bankAccount, branchName: e.target.value }
            })}
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => {
    // Show success screen after contract submission
    if (contractSubmitted) {
      return (
        <div>
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
            <div style={{
              width: 'clamp(80px, 20vw, 100px)',
              height: 'clamp(80px, 20vw, 100px)',
              background: 'var(--gradient-success)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-xl)',
              fontSize: 'clamp(2rem, 8vw, 3rem)'
            }}>
              ✓
            </div>
            <h2 style={{ color: 'var(--success)', marginBottom: 'var(--space-md)' }}>
              🎉 Onboarding Complete!
            </h2>
            <p style={{ fontSize: 'var(--font-size-lg)', color: 'var(--gray-600)' }}>
              Your restaurant registration has been successfully submitted
            </p>
          </div>

          <div className="card" style={{ marginBottom: 'var(--space-xl)', background: 'rgba(79, 205, 196, 0.05)' }}>
            <h3 style={{ marginBottom: 'var(--space-lg)', color: 'var(--success)' }}>📄 Your Contract</h3>
            <p style={{ marginBottom: 'var(--space-lg)', color: 'var(--gray-700)' }}>
              Your partner agreement has been generated. Download and keep it for your records.
            </p>
            <button
              onClick={handleDownloadContract}
              style={{
                width: '100%',
                padding: 'var(--space-lg)',
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-lg)',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-md)'
              }}
            >
              📥 Download Partner Agreement
            </button>
          </div>

          <div className="card" style={{ marginBottom: 'var(--space-xl)' }}>
            <h3 style={{ marginBottom: 'var(--space-lg)' }}>📋 What's Next?</h3>
            <ol style={{ paddingLeft: 'var(--space-xl)', marginBottom: 0 }}>
              <li style={{ marginBottom: 'var(--space-md)' }}>
                <strong>Document Review:</strong> Our team will review your submitted documents
              </li>
              <li style={{ marginBottom: 'var(--space-md)' }}>
                <strong>Verification:</strong> We'll verify your PAN, GST, and FSSAI details
              </li>
              <li style={{ marginBottom: 'var(--space-md)' }}>
                <strong>Approval:</strong> Once verified, your restaurant will be approved
              </li>
              <li style={{ marginBottom: 'var(--space-md)' }}>
                <strong>Go Live:</strong> Your restaurant will be live on Foodwinc platform!
              </li>
            </ol>
          </div>

          <div style={{
            background: 'rgba(79, 70, 229, 0.05)',
            border: '2px solid var(--primary)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-lg)',
            textAlign: 'center'
          }}>
            <h4 style={{ marginTop: 0, color: 'var(--primary)' }}>⏰ Estimated Timeline</h4>
            <p style={{ fontSize: 'var(--font-size-lg)', marginBottom: 0 }}>
              Review process typically takes <strong>24-48 hours</strong>
            </p>
            <p style={{ color: 'var(--gray-600)', marginTop: 'var(--space-sm)', marginBottom: 0 }}>
              We'll notify you via email and SMS once your restaurant is approved
            </p>
          </div>
        </div>
      );
    }

    // Show contract acceptance form
    return (
      <div>
        <h2 style={{ marginBottom: 'var(--space-xl)', color: 'var(--gray-900)' }}>
          📜 Review & Contract
        </h2>

        <div className="card" style={{ marginBottom: 'var(--space-xl)', background: 'rgba(79, 205, 196, 0.05)' }}>
          <h3 style={{ marginBottom: 'var(--space-lg)', color: 'var(--success)' }}>✓ Your Details</h3>
          <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
            <div>
              <strong>Cuisines:</strong> {selectedCuisines.join(', ')}
            </div>
            <div>
              <strong>Device Type:</strong> {deviceType.replace('_', ' ').toUpperCase()}
            </div>
            <div>
              <strong>PAN:</strong> {documents.pan.number} ({documents.pan.holderName})
            </div>
            <div>
              <strong>GST:</strong> {documents.gst.number}
            </div>
            <div>
              <strong>FSSAI:</strong> {documents.fssai.number}
            </div>
            <div>
              <strong>Bank Account:</strong> {documents.bankAccount.accountNumber} - {documents.bankAccount.bankName}
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 'var(--space-xl)' }}>
          <h3 style={{ marginBottom: 'var(--space-lg)' }}>📋 Partner Agreement</h3>
          <div style={{
            maxHeight: '500px',
            overflowY: 'auto',
            padding: 'var(--space-lg)',
            background: 'var(--gray-50)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--gray-200)',
            marginBottom: 'var(--space-lg)'
          }}>
            <h4 style={{ textAlign: 'center', marginBottom: 'var(--space-lg)' }}>FOODWINC PARTNER AGREEMENT</h4>
            <p>This agreement is entered into between Foodwinc ("Platform") and the Restaurant Partner ("Partner").</p>

            <h5>1. Service Agreement</h5>
            <p>The Partner agrees to list their restaurant and menu on the Foodwinc platform and fulfill orders received through the platform in a timely and professional manner.</p>

            <h5>2. Subscription Model (Zero Commission)</h5>
            <p>The Platform operates on a zero-commission model. Partners are charged <strong>0% commission</strong> on order value (excluding taxes and delivery charges). Instead, Partners pay a monthly subscription fee based on restaurant category and location. Detailed subscription plans are provided separately during onboarding.</p>

            <h5>3. Quality Standards</h5>
            <p>The Partner agrees to maintain high standards of food quality, hygiene, and packaging. All food items must comply with FSSAI regulations and local health and safety standards.</p>

            <h5>4. Order Acceptance</h5>
            <p>The Partner will use the designated device type ({deviceType.replace('_', ' ').toUpperCase()}) to receive and manage orders. Orders must be accepted or rejected within 2 minutes of receipt.</p>

            <h5>5. Preparation Time</h5>
            <p>The Partner commits to prepare orders within the specified preparation time of {deliveryTimings.preparationTime || 30} minutes. Any delays must be communicated immediately through the platform.</p>

            <h5>6. Menu Management</h5>
            <p>The Partner is responsible for keeping menu items, prices, and availability updated on the platform. Any changes must be reflected within 24 hours.</p>

            <h5>7. Payment Terms</h5>
            <p>Order payments will be settled weekly through the registered bank account. The Platform will deduct applicable subscription fees, taxes, and any other charges before settlement. Subscription fees are billed monthly in advance.</p>

            <h5>8. Settlement Processing Fee</h5>
            <p>A Settlement Processing Fee of <strong>3% (Three Percent)</strong> shall be applicable on each payout amount. Additionally, GST at the prevailing rate of <strong>18%</strong> shall be levied on the Settlement Processing Fee, resulting in an effective fee of <strong>3.54%</strong> (3% + 18% GST on 3%). This fee covers payment processing, banking charges, and administrative costs associated with fund transfers. The Settlement Processing Fee along with applicable GST shall be deducted from the payout amount before disbursement. A GST-compliant invoice for the Settlement Processing Fee will be generated and provided to the Partner for each payout under HSN/SAC Code 998319 (Other Financial Services). The Partner acknowledges and agrees to this deduction as a condition of receiving payouts through the Foodwinc platform.</p>

            <h5>9. Cancellations and Refunds</h5>
            <p>The Partner may cancel orders only in exceptional circumstances. Excessive cancellations may result in penalties or suspension. Customer refunds due to Partner issues will be borne by the Partner.</p>

            <h5>10. Operating Hours</h5>
            <p>The Partner will operate during the declared operating hours as configured in the platform. Any changes must be updated on the platform.</p>

            <h5>11. Intellectual Property</h5>
            <p>The Partner grants Foodwinc a non-exclusive license to use restaurant name, logo, and menu images for marketing purposes on the platform and promotional materials.</p>

            <h5>12. Data and Privacy</h5>
            <p>Both parties agree to comply with applicable data protection laws. Customer data will be used solely for order fulfillment and service improvement.</p>

            <h5>13. Termination</h5>
            <p>Either party may terminate this agreement with 30 days written notice. The Platform reserves the right to terminate immediately in case of serious violations of terms or fraudulent activities. Upon termination, pending settlements will be processed within 7 business days.</p>

            <h5>14. Honest Pricing Policy</h5>
            <p>The Partner agrees to maintain <strong>price parity</strong> across all channels. Menu item prices on the Foodwinc platform must be identical to the restaurant's dine-in and takeaway prices. Inflating prices on the platform is strictly prohibited and constitutes a violation of this agreement, which may result in immediate suspension or termination without notice.</p>

            <h5>15. Liability</h5>
            <p>The Partner is solely responsible for food quality, safety, and compliance with regulations. The Platform is not liable for any food-related incidents or customer complaints arising from Partner negligence.</p>

            <h5>16. Dispute Resolution</h5>
            <p>Any disputes arising from this agreement will be resolved through arbitration in accordance with Indian Arbitration and Conciliation Act, 1996. The jurisdiction will be AGRA, Uttar Pradesh, India.</p>

            <h5>17. Amendment</h5>
            <p>Foodwinc reserves the right to modify these terms with 15 days notice to Partners. Continued use of the platform after modifications constitutes acceptance of new terms.</p>

            <p style={{ marginTop: 'var(--space-xl)', fontWeight: '600', textAlign: 'center' }}>
              By accepting this agreement, you confirm that you have read, understood, and agree to abide by all terms and conditions stated herein.
            </p>
          </div>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
            padding: 'var(--space-lg)',
            background: contractAccepted ? 'rgba(79, 205, 196, 0.1)' : 'white',
            border: `2px solid ${contractAccepted ? 'var(--success)' : 'var(--gray-300)'}`,
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={contractAccepted}
              onChange={(e) => setContractAccepted(e.target.checked)}
              style={{ width: '20px', height: '20px' }}
            />
            <span style={{ fontWeight: '600', fontSize: 'var(--font-size-lg)' }}>
              I accept the Partner Agreement and certify that all information provided is accurate
            </span>
          </label>
        </div>

        <div style={{
          background: 'rgba(79, 70, 229, 0.05)',
          border: '2px solid var(--primary)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-lg)'
        }}>
          <h4 style={{ marginTop: 0, color: 'var(--primary)' }}>📱 Next Steps</h4>
          <ol style={{ marginBottom: 0, paddingLeft: 'var(--space-xl)' }}>
            <li>Review and accept the partner agreement above</li>
            <li>Submit to generate your digital contract</li>
            <li>Your restaurant will be reviewed by our team</li>
            <li>Once approved, your restaurant will go LIVE on Foodwinc!</li>
          </ol>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', padding: '32px 16px' }}>
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 10px 24px rgba(0,0,0,0.05)',
          animation: 'fadeIn 0.3s ease-in'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '800',
              color: '#0F172A',
              marginBottom: '8px',
              letterSpacing: '-0.5px'
            }}>
              🏪 Restaurant Onboarding
            </h1>
            <p style={{ color: '#64748B', fontSize: '16px', marginTop: '8px' }}>
              Complete all steps to list your restaurant on Foodwinc
            </p>
          </div>

          {renderProgressBar()}

          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '24px',
              color: '#DC2626',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {error}
            </div>
          )}

          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  background: '#FFFFFF',
                  color: '#64748B',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: loading ? 0.5 : 1
                }}
                onMouseEnter={(e) => !loading && (e.target.style.background = '#F8FAFC')}
                onMouseLeave={(e) => !loading && (e.target.style.background = '#FFFFFF')}
              >
                ← Previous
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={loading}
              style={{
                flex: 2,
                padding: '14px 20px',
                background: loading ? '#94A3B8' : '#FF6961',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: loading ? 'none' : '0 2px 8px rgba(255, 105, 97, 0.25)'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#FF5449')}
              onMouseLeave={(e) => !loading && (e.target.style.background = '#FF6961')}
            >
              {loading ? '⏳ Saving...' : currentStep === 4 ? '📤 Submit Onboarding' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RestaurantOnboarding;
